import React, {useState} from 'react';
import {List, useGetList, usePermissions, useRedirect, useListContext, useTranslate} from 'react-admin';
import {Alert, AlertTitle, Avatar, Box, MenuItem, Pagination, Select, TextField as MuiTextField, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {AddRounded, CloudUploadOutlined, TranslateOutlined} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import SavedViewsTabs from '../components/statamic/SavedViewsTabs';
import FilterBar, {FilterDropdown} from '../components/statamic/FilterBar';
import StatamicTable from '../components/statamic/StatamicTable';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import MonoChip from '../components/statamic/MonoChip';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';

const OrgCell = ({record}) => {
    const logoUrl = record.logo_name && record.ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.logo_name}.${record.ext}`
        : undefined;

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Avatar
                src={logoUrl}
                variant="rounded"
                sx={{
                    width: 28,
                    height: 28,
                    fontSize: 13,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                }}
            >
                {record.charity_name?.charAt(0)}
            </Avatar>
            <Box sx={{minWidth: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <StatusDot variant={record.active ? 'success' : 'danger'}/>
                    <Typography
                        sx={{
                            fontSize: `${t.type.bodyStrong.size}px`,
                            fontWeight: t.type.bodyStrong.weight,
                            color: t.color.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {record.charity_name}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const LocationCell = ({record}) => (
    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
        {[record.city, record.country].filter(Boolean).join(', ') || '—'}
    </Typography>
);

const EmailCell = ({record}) => (
    <Typography
        sx={{
            fontSize: `${t.type.body.size}px`,
            color: record.email ? t.color.accent.primary : t.color.text.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}
    >
        {record.email || '—'}
    </Typography>
);

const StatamicPagination = () => {
    const {page, perPage, total, setPage} = useListContext();
    const translate = useTranslate();
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                {translate('Showing page %{page} of %{pageCount} · %{total} total', {page, pageCount, total: total ?? 0})}
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.status.infoTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

const SurfaceCard = ({children}) => (
    <Box
        sx={{
            backgroundColor: t.color.surface,
            border: `1px solid ${t.color.border.default}`,
            borderRadius: `${t.radius.md}px`,
            mx: 3,
            mb: 3,
            overflow: 'hidden',
        }}
    >
        {children}
    </Box>
);

// Interim dual id-space (slice 5 replaces this whole tools panel): the field
// this feeds a v2 locations fetch (wants the umbrella's UUID) as well as two
// v1 POSTs (want the legacy numeric id) — no single value satisfies both.
const UMBRELLA_ID_HELPER_TEXT = 'Paste the umbrella UUID to load locations. Translate and import still use the legacy numeric id and move to v2 soon';

const toolFieldSx = {
    flex: 1,
    minWidth: 120,
    '& .MuiInputBase-input': {fontSize: `${t.type.body.size}px`},
};

const toolButtonSx = {
    textTransform: 'none',
    fontSize: `${t.type.small.size}px`,
    borderColor: t.color.border.default,
    color: t.color.text.primary,
    '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
};

const UmbrellaAdminTools = () => {
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const canUse = hasPermission(permissions, ['show'], 'umbrella');

    const [umbrellaCharityId, setUmbrellaCharityId] = useState('');
    const [languageCode, setLanguageCode] = useState('');
    const [locationId, setLocationId] = useState('');
    const [sending, setSending] = useState(false);
    const [importing, setImporting] = useState(false);
    const [status, setStatus] = useState(null); // {type: 'success' | 'warning', text}

    // Interim dual id-space (slice 5 replaces this whole panel): this
    // useGetList wants `umbrellaCharityId` to be the umbrella's UUID (v2
    // `umbrella_id` filter), but `translateNonTranslated`/`importData` below
    // POST that same value as the legacy NUMERIC id. No single value the admin
    // types satisfies both — see the helper text on the field below.
    const {data: locations} = useGetList(
        'locations',
        {
            filter: {umbrella_id: umbrellaCharityId},
            sort: {field: 'title', order: 'ASC'},
            pagination: {page: 1, perPage: 100},
        },
        {enabled: canUse && !!umbrellaCharityId},
    );

    if (!canUse) return null;

    const locationChoices = (locations || []).map((l) => ({id: l.id, name: l.title}));

    const reportError = (reason) => {
        setStatus({type: 'warning', text: typeof reason === 'string' ? reason : (reason?.message ?? translate('Something went wrong'))});
    };

    const translateNonTranslated = () => {
        setSending(true);
        setStatus(null);
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v1/admin/translateNonTranslated/${umbrellaCharityId}/${languageCode}`
        ).then((response) => {
            setSending(false);
            if (response.status === 200) setStatus({type: 'success', text: translate('All content has been translated')});
            else setStatus({type: 'warning', text: ''});
        }).catch((reason) => { setSending(false); reportError(reason); });
    };

    const importData = () => {
        setImporting(true);
        setStatus(null);
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v1/admin/importData/${umbrellaCharityId}/${locationId}`
        ).then((response) => {
            setImporting(false);
            if (response.status === 200) setStatus({type: 'success', text: translate('Data has been imported successfully')});
            else setStatus({type: 'warning', text: ''});
        }).catch((reason) => { setImporting(false); reportError(reason); });
    };

    return (
        <Box sx={{mx: 3, mb: 3}}>
            <SectionCard title={translate('Admin Tools')}>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 3}}>
                    <Box>
                        <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 600, color: t.color.text.primary, mb: 0.5}}>
                            {translate('Translation Service')}
                        </Typography>
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, mb: 1.5}}>
                            {translate('Translate all untranslated content for an umbrella organization')}
                        </Typography>
                        <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center'}}>
                            <MuiTextField
                                label={translate('Umbrella ID')}
                                variant="outlined"
                                size="small"
                                value={umbrellaCharityId}
                                onChange={(e) => setUmbrellaCharityId(e.target.value)}
                                helperText={translate('umbrella.id_helper_text', {_: UMBRELLA_ID_HELPER_TEXT})}
                                sx={toolFieldSx}
                            />
                            <MuiTextField
                                label={translate('Target Language')}
                                variant="outlined"
                                size="small"
                                placeholder="nl, fr, de"
                                value={languageCode}
                                onChange={(e) => setLanguageCode(e.target.value)}
                                sx={toolFieldSx}
                            />
                            <LoadingButton
                                onClick={translateNonTranslated}
                                loading={sending}
                                disabled={!umbrellaCharityId || !languageCode}
                                variant="outlined"
                                size="small"
                                startIcon={<TranslateOutlined sx={{fontSize: 16}}/>}
                                sx={toolButtonSx}
                            >
                                {translate('Translate')}
                            </LoadingButton>
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 600, color: t.color.text.primary, mb: 0.5}}>
                            {translate('Data Import')}
                        </Typography>
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, mb: 1.5}}>
                            {translate('Import data for a specific umbrella organization and location')}
                        </Typography>
                        <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center'}}>
                            <MuiTextField
                                label={translate('Umbrella ID')}
                                variant="outlined"
                                size="small"
                                value={umbrellaCharityId}
                                onChange={(e) => setUmbrellaCharityId(e.target.value)}
                                helperText={translate('umbrella.id_helper_text', {_: UMBRELLA_ID_HELPER_TEXT})}
                                sx={toolFieldSx}
                            />
                            {umbrellaCharityId !== '' && locationChoices.length > 0 && (
                                <Select
                                    value={locationId}
                                    size="small"
                                    displayEmpty
                                    onChange={(e) => setLocationId(e.target.value)}
                                    sx={{flex: 1, minWidth: 150, fontSize: `${t.type.body.size}px`, backgroundColor: t.color.surface}}
                                >
                                    <MenuItem value="" disabled>{translate('Select Location')}</MenuItem>
                                    {locationChoices.map((location) => (
                                        <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                                    ))}
                                </Select>
                            )}
                            <LoadingButton
                                onClick={importData}
                                loading={importing}
                                disabled={!umbrellaCharityId || !locationId}
                                variant="outlined"
                                size="small"
                                startIcon={<CloudUploadOutlined sx={{fontSize: 16}}/>}
                                sx={toolButtonSx}
                            >
                                {translate('Import')}
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>

                {status && (
                    <Alert severity={status.type} onClose={() => setStatus(null)} sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                        <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>
                            {status.type === 'success' ? translate('Success') : translate('Warning')}
                        </AlertTitle>
                        {status.text}
                    </Alert>
                )}
            </SectionCard>
        </Box>
    );
};

const UmbrellaListStatamicInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const [activeViewId, setActiveViewId] = useState('all');

    const columns = [
        { key: 'org',      label: translate('Umbrella'), sortable: true, sortField: 'charity_name', render: (r) => <OrgCell record={r}/> },
        { key: 'location', label: translate('Location'),     render: (r) => <LocationCell record={r}/> },
        { key: 'email',    label: translate('Email'),        render: (r) => <EmailCell record={r}/> },
        { key: 'payment',  label: translate('Payment'),      render: (r) => r.payment_link_url
            ? <MonoChip>{translate('configured')}</MonoChip>
            : <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>{translate('not configured')}</Typography> },
        { key: 'status',   label: translate('Status'),       width: 120, render: (r) => r.demo_charity
            ? <StatusPill variant="info">{translate('Demo')}</StatusPill>
            : <StatusPill variant={r.active ? 'success' : 'neutral'}>{r.active ? translate('Active') : translate('Inactive')}</StatusPill> },
    ];

    const views = [
        { id: 'all',    label: translate('All') },
        { id: 'active', label: translate('Active') },
        { id: 'demo',   label: translate('Demo') },
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = { ...filterValues };
        delete base.active;
        delete base.demo_charity;
        if (id === 'active')      setFilters({ ...base, active: true }, displayedFilters);
        else if (id === 'demo')   setFilters({ ...base, demo_charity: true }, displayedFilters);
        else                      setFilters(base, displayedFilters);
    };

    const rowActions = (record) => [
        { label: translate('View'), onClick: () => redirect('show', 'umbrellas', record.id) },
        { label: translate('Edit'), onClick: () => redirect('edit', 'umbrellas', record.id) },
    ];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Umbrellas')}
                subtitle={translate('Umbrella organizations and their sponsorship configuration')}
                actions={
                    <PrimaryButton
                        variant="primary"
                        startIcon={<AddRounded sx={{fontSize: 18}}/>}
                        onClick={() => redirect('create', 'umbrellas')}
                    >
                        {translate('Create umbrella')}
                    </PrimaryButton>
                }
            />

            <SurfaceCard>
                <SavedViewsTabs
                    views={views}
                    activeId={activeViewId}
                    onSelect={handleViewSelect}
                    onAdd={() => {}}
                />

                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({ ...filterValues, q }, displayedFilters)}
                    placeholder={translate('Search charities')}
                    filters={
                        <>
                            <FilterDropdown
                                label={translate('Active')}
                                source="active"
                                type="boolean"
                                value={filterValues?.active}
                                onChange={(src, v) => setFilters({...filterValues, [src]: v}, displayedFilters)}
                                onClear={(src) => {
                                    const next = {...filterValues};
                                    delete next[src];
                                    setFilters(next, displayedFilters);
                                }}
                            />
                            <FilterDropdown
                                label={translate('Demo')}
                                source="demo_charity"
                                type="boolean"
                                value={filterValues?.demo_charity}
                                onChange={(src, v) => setFilters({...filterValues, [src]: v}, displayedFilters)}
                                onClear={(src) => {
                                    const next = {...filterValues};
                                    delete next[src];
                                    setFilters(next, displayedFilters);
                                }}
                            />
                        </>
                    }
                />

                <StatamicTable
                    columns={columns}
                    rowActions={rowActions}
                    onRowClick={(record) => redirect('show', 'umbrellas', record.id)}
                />

                <StatamicPagination/>
            </SurfaceCard>

            <UmbrellaAdminTools/>
        </Box>
    );
};

const UmbrellaListStatamic = (props) => (
    <List
        {...props}
        title="Umbrellas"
        sort={{field: 'create_date_time', order: 'DESC'}}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': { backgroundColor: t.color.page },
        }}
    >
        <UmbrellaListStatamicInner/>
    </List>
);

export default UmbrellaListStatamic;
